/**
 * 根据「可视时间跨度」选择较易读的主刻度步长（nice number）。
 *
 * @param visibleSpanSec - 横向一屏大约对应的秒数
 */
export function pickMajorStepSeconds(visibleSpanSec: number): number {
  const span = Math.max(0.05, Number(visibleSpanSec) || 1)
  const target = span / 10
  const pow10 = 10 ** Math.floor(Math.log10(target))
  const frac = target / pow10
  let nice = 1
  if (frac <= 1.5) nice = 1
  else if (frac <= 3) nice = 2
  else if (frac <= 7) nice = 5
  else nice = 10
  return nice * pow10
}

export type RulerTickSet = {
  /** 主刻度（秒），通常显示标签 */
  majorSeconds: number[]
  /** 次刻度（秒），可不显示标签 */
  minorSeconds: number[]
}

/**
 * 与 {@link buildRulerTickSet} 相同，但主刻度步长不低于 `minMajorStepSec`（用于「最大缩放仅秒级刻度」）。
 *
 * @param timelineMaxSec - 时间轴总时长
 * @param visibleSpanSec - 一屏可视秒数
 * @param minMajorStepSec - 主刻度最小间隔（秒），例如 `1`
 */
export function buildRulerTickSetMinMajorStep(
  timelineMaxSec: number,
  visibleSpanSec: number,
  minMajorStepSec: number,
): RulerTickSet {
  const maxT = Math.max(0, Number(timelineMaxSec))
  const majorStep = Math.max(minMajorStepSec, pickMajorStepSeconds(visibleSpanSec))
  const majorSeconds: number[] = []
  for (let t = 0; t <= maxT + 1e-9; t += majorStep) {
    majorSeconds.push(Number(t.toPrecision(10)))
  }
  const minorSeconds: number[] = []
  if (majorStep >= 1) {
    const minorStep = majorStep / (majorStep >= 5 ? 5 : 2)
    const estCount = Math.ceil(maxT / minorStep)
    if (estCount <= 240 && minorStep >= 1 - 1e-9) {
      for (let t = 0; t <= maxT + 1e-9; t += minorStep) {
        const rounded = Number(t.toPrecision(10))
        const isMajor = majorSeconds.some((m) => Math.abs(m - rounded) < minorStep * 0.15)
        if (!isMajor) minorSeconds.push(rounded)
      }
    }
  }
  return { majorSeconds, minorSeconds }
}

/**
 * 生成主、次刻度（秒），覆盖 `[0, timelineMaxSec]`。
 *
 * @param timelineMaxSec - 时间轴总时长
 * @param visibleSpanSec - 一屏可视秒数（越小刻度越密）
 */
export function buildRulerTickSet(timelineMaxSec: number, visibleSpanSec: number): RulerTickSet {
  const maxT = Math.max(0, Number(timelineMaxSec))
  const majorStep = pickMajorStepSeconds(visibleSpanSec)
  const majorSeconds: number[] = []
  for (let t = 0; t <= maxT + 1e-9; t += majorStep) {
    majorSeconds.push(Number(t.toPrecision(10)))
  }
  const minorSeconds: number[] = []
  if (majorStep >= 1) {
    const minorStep = majorStep / (majorStep >= 5 ? 5 : 2)
    const estCount = Math.ceil(maxT / minorStep)
    if (estCount <= 240) {
      for (let t = 0; t <= maxT + 1e-9; t += minorStep) {
        const rounded = Number(t.toPrecision(10))
        const isMajor = majorSeconds.some((m) => Math.abs(m - rounded) < minorStep * 0.15)
        if (!isMajor) minorSeconds.push(rounded)
      }
    }
  }
  return { majorSeconds, minorSeconds }
}
